import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MonthCalendar } from "@/components/MonthCalendar";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDateIT, monthKey } from "@/lib/dates";

export default function AdminHolidays() {
  const [cursor, setCursor] = useState(new Date());
  const [holidays, setHolidays] = useState([]);
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    const res = await api.get(`/holidays?month=${monthKey(cursor)}`);
    setHolidays(res.data);
  };

  useEffect(() => {
    load().catch(() => toast.error("Errore nel caricamento"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor]);

  const move = (delta) => {
    const d = new Date(cursor);
    d.setMonth(d.getMonth() + delta);
    setCursor(d);
  };

  const setStatus = async (h, status) => {
    setBusyId(h.id);
    try {
      await api.put(`/holidays/${h.id}/status`, { status });
      toast.success(status === "accepted" ? "Ferie accettate" : "Ferie rifiutate");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  const pending = holidays.filter((h) => h.status === "pending");
  const resolved = holidays.filter((h) => h.status !== "pending");

  return (
    <div className="space-y-5">
      <h1 className="font-head text-2xl font-bold text-slate-900">Ferie</h1>

      <MonthCalendar date={cursor} onPrev={() => move(-1)} onNext={() => move(1)} holidays={holidays} />

      <div>
        <h2 className="font-head text-lg font-bold text-slate-900 mb-2">Da approvare</h2>
        <div className="space-y-2">
          {pending.length === 0 && <p className="text-slate-400 text-sm">Nessuna richiesta in attesa.</p>}
          {pending.map((h) => (
            <div key={h.id} data-testid="pending-holiday" className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-semibold text-slate-900 text-lg">{h.employee_name}</p>
                  <p className="text-sm text-slate-500">{formatDateIT(h.start_date)} → {formatDateIT(h.end_date)}</p>
                </div>
                <StatusBadge status={h.status} />
              </div>
              <div className="flex gap-2">
                <button data-testid={`reject-${h.id}`} onClick={() => setStatus(h, "rejected")} disabled={busyId === h.id} className="flex-1 h-12 rounded-xl border border-red-200 text-destructive font-bold flex items-center justify-center gap-1.5 active:scale-[0.98]">
                  <X className="h-5 w-5" /> Rifiuta
                </button>
                <button data-testid={`accept-${h.id}`} onClick={() => setStatus(h, "accepted")} disabled={busyId === h.id} className="flex-1 h-12 rounded-xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-1.5 active:scale-[0.98]">
                  {busyId === h.id ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Check className="h-5 w-5" /> Accetta</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {resolved.length > 0 && (
        <div>
          <h2 className="font-head text-lg font-bold text-slate-900 mb-2">Gestite</h2>
          <div className="space-y-2">
            {resolved.map((h) => (
              <div key={h.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-900">{h.employee_name}</p>
                  <p className="text-sm text-slate-500">{formatDateIT(h.start_date)} → {formatDateIT(h.end_date)}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <StatusBadge status={h.status} />
                  {h.status === "accepted" && (
                    <button onClick={() => setStatus(h, "rejected")} className="text-xs text-slate-400 underline">Rifiuta</button>
                  )}
                  {h.status === "rejected" && (
                    <button onClick={() => setStatus(h, "accepted")} className="text-xs text-slate-400 underline">Accetta</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
